// GSAP Animation Setup with TextPlugin
// Provides typed text effects, glitch animations, and scroll-triggered reveals
// For "The House of Chopras" - Terminal aesthetic animations

import { gsap } from 'gsap';
import { TextPlugin } from 'gsap/TextPlugin';

// Register GSAP plugins
gsap.registerPlugin(TextPlugin);

// Check for reduced motion preference - disabled to ensure full experience
export const prefersReducedMotion = (): boolean => {
  return false;
};

// Scramble characters for text effect
const scrambleChars = '!@#$%^&*()_+-=[]{}|;:,.<>?/~`0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Get a random character from the scramble set
 */
const getRandomChar = (): string => {
  return scrambleChars[Math.floor(Math.random() * scrambleChars.length)];
};

/**
 * Type text into an element with a typewriter effect
 * @param element - The target HTML element
 * @param text - The text to type
 * @param duration - Duration in seconds (auto-calculated based on text length if not provided)
 * @param options - Additional options for the text animation
 */
export const typeText = (
  element: HTMLElement,
  text: string,
  duration?: number,
  options?: {
    delimiter?: string;
    onComplete?: () => void;
  }
): gsap.core.Tween => {
  // Calculate duration based on text length if not provided (approximately 50ms per character)
  const calculatedDuration = duration ?? text.length * 0.05;

  // If user prefers reduced motion, show text instantly
  if (prefersReducedMotion()) {
    element.textContent = text;
    options?.onComplete?.();
    return gsap.to(element, { duration: 0 });
  }

  return gsap.to(element, {
    duration: calculatedDuration,
    text: {
      value: text,
      delimiter: options?.delimiter ?? '',
    },
    ease: 'none',
    onComplete: options?.onComplete,
  });
};

/**
 * Type text with a cursor effect
 * @param element - The target HTML element (should contain a child element for cursor)
 * @param text - The text to type
 * @param cursorElement - The cursor element to show during typing
 */
export const typeTextWithCursor = (
  element: HTMLElement,
  text: string,
  cursorElement?: HTMLElement,
  options?: {
    duration?: number;
    onComplete?: () => void;
  }
): gsap.core.Tween => {
  // Show cursor during typing
  if (cursorElement) {
    cursorElement.style.display = 'inline-block';
  }

  return typeText(element, text, options?.duration, {
    onComplete: () => {
      // Optionally hide cursor after typing
      options?.onComplete?.();
    },
  });
};

/**
 * Create a glitch effect on an element
 * @param element - The target HTML element
 * @param intensity - Intensity of the glitch (1-10)
 */
export const glitch = (
  element: HTMLElement,
  intensity: number = 5
): gsap.core.Timeline => {
  // If user prefers reduced motion, skip the animation
  if (prefersReducedMotion()) {
    return gsap.timeline();
  }

  const skewAmount = intensity * 2;
  const tl = gsap.timeline();

  tl.to(element, {
    skewX: skewAmount,
    duration: 0.05,
    ease: 'power2.inOut',
  })
    .to(element, {
      skewX: -skewAmount,
      filter: 'hue-rotate(90deg)',
      duration: 0.05,
    })
    .to(element, {
      skewX: skewAmount / 2,
      x: intensity,
      duration: 0.05,
    })
    .to(element, {
      skewX: 0,
      x: 0,
      filter: 'none',
      duration: 0.05,
    });

  return tl;
};

/**
 * Create a terminal-style typing effect with multiple lines
 * @param container - The container element
 * @param lines - Array of lines to type
 * @param lineDelay - Delay between lines in seconds
 */
export const typeTerminalLines = async (
  container: HTMLElement,
  lines: Array<{ text: string; class?: string; delay?: number }>,
  lineDelay: number = 0.5
): Promise<void> => {
  for (const line of lines) {
    const lineElement = document.createElement('div');
    lineElement.className = line.class ?? 'terminal-line';
    container.appendChild(lineElement);

    // Add optional delay before typing this line
    if (line.delay !== undefined) {
      const delayMs = line.delay * 1000;
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }

    // Type the line
    await new Promise<void>(resolve => {
      typeText(lineElement, line.text, undefined, { onComplete: resolve });
    });

    // Wait before next line
    await new Promise(resolve => setTimeout(resolve, lineDelay * 1000));
  }
};

/**
 * Create a scramble text effect (using GSAP's text plugin with word delimiter)
 * @param element - The target HTML element
 * @param text - The final text to reveal
 * @param duration - Duration of the effect
 */
export const scrambleReveal = (
  element: HTMLElement,
  text: string,
  duration: number = 1.5
): gsap.core.Tween => {
  if (prefersReducedMotion()) {
    element.textContent = text;
    return gsap.to(element, { duration: 0 });
  }

  return gsap.to(element, {
    duration,
    text: {
      value: text,
      type: 'diff',
    },
    ease: 'none',
  });
};

/**
 * Fade in element from below
 * @param element - The target HTML element
 * @param duration - Duration in seconds
 * @param delay - Delay before animation starts
 */
export const fadeInUp = (
  element: HTMLElement,
  duration: number = 0.6,
  delay: number = 0
): gsap.core.Tween => {
  if (prefersReducedMotion()) {
    gsap.set(element, { opacity: 1, y: 0 });
    return gsap.to(element, { duration: 0 });
  }

  gsap.set(element, { opacity: 0, y: 30 });
  return gsap.to(element, {
    opacity: 1,
    y: 0,
    duration,
    delay,
    ease: 'power2.out',
  });
};

/**
 * Create a flickering/pulsing effect (like old CRT monitors)
 * @param element - The target HTML element
 * @param duration - Total duration of one flicker cycle
 */
export const flicker = (
  element: HTMLElement,
  duration: number = 0.1
): gsap.core.Timeline => {
  if (prefersReducedMotion()) {
    return gsap.timeline();
  }

  const tl = gsap.timeline({ repeat: -1, repeatDelay: Math.random() * 2 + 1 });

  tl.to(element, {
    opacity: 0.7,
    duration: duration / 2,
    ease: 'steps(1)',
  }).to(element, {
    opacity: 1,
    duration: duration / 2,
    ease: 'steps(1)',
  });

  return tl;
};

/**
 * Reveal text character by character with optional glitch on each character
 * @param element - The target HTML element
 * @param text - The text to reveal
 * @param charDelay - Delay between characters in seconds
 * @param glitchChance - Probability (0-1) that each character will glitch
 */
export const revealWithGlitch = async (
  element: HTMLElement,
  text: string,
  charDelay: number = 0.03,
  glitchChance: number = 0.1
): Promise<void> => {
  if (prefersReducedMotion()) {
    element.textContent = text;
    return;
  }

  let currentText = '';
  for (const char of text) {
    currentText += char;
    element.textContent = currentText;

    // Random chance to glitch
    if (Math.random() < glitchChance) {
      await new Promise(resolve => {
        glitch(element, 3).then(resolve);
      });
    }

    await new Promise(resolve => setTimeout(resolve, charDelay * 1000));
  }
};

/**
 * Create a blinking cursor effect
 * @param element - The cursor element
 * @param blinkRate - Blink rate in seconds (default 0.53s for authentic terminal feel)
 */
export const createBlinkingCursor = (
  element: HTMLElement,
  blinkRate: number = 0.53
): gsap.core.Timeline => {
  if (prefersReducedMotion()) {
    element.style.opacity = '1';
    return gsap.timeline();
  }

  const tl = gsap.timeline({ repeat: -1 });

  tl.to(element, {
    opacity: 0,
    duration: 0,
    delay: blinkRate,
  }).to(element, {
    opacity: 1,
    duration: 0,
    delay: blinkRate,
  });

  return tl;
};

/**
 * Stop cursor blinking (usually when typing starts)
 * @param cursorTimeline - The timeline returned by createBlinkingCursor
 */
export const stopCursorBlink = (cursorTimeline: gsap.core.Timeline): void => {
  cursorTimeline.kill();
};

/**
 * Text scramble effect - reveals text through random characters
 * More sophisticated than simple typing for dramatic reveals
 * @param element - The target HTML element
 * @param text - The final text to reveal
 * @param duration - Total duration of the effect
 * @param scrambleDuration - How long each character scrambles before settling
 */
export const textScramble = (
  element: HTMLElement,
  text: string,
  duration: number = 2,
  _scrambleDuration: number = 0.3
): Promise<void> => {
  return new Promise(resolve => {
    if (prefersReducedMotion()) {
      element.textContent = text;
      resolve();
      return;
    }

    const chars = text.split('');
    const totalChars = chars.length;
    const charRevealInterval = duration / totalChars;
    let currentIndex = 0;
    let intervalId: number;

    const updateText = () => {
      let result = '';
      for (let i = 0; i < totalChars; i++) {
        if (i < currentIndex) {
          result += chars[i];
        } else if (i === currentIndex) {
          result += getRandomChar();
        } else {
          result += Math.random() > 0.7 ? getRandomChar() : ' ';
        }
      }
      element.textContent = result;
    };

    // Scramble effect
    intervalId = window.setInterval(() => {
      updateText();
    }, 30);

    // Reveal characters one by one
    const revealInterval = window.setInterval(() => {
      currentIndex++;
      if (currentIndex >= totalChars) {
        window.clearInterval(revealInterval);
        window.clearInterval(intervalId);
        element.textContent = text;
        resolve();
      }
    }, charRevealInterval * 1000);
  });
};

/**
 * Type text with a pause effect (like someone thinking)
 * @param element - The target HTML element
 * @param segments - Array of text segments with optional pauses
 */
export const typeWithPauses = async (
  element: HTMLElement,
  segments: Array<{ text: string; pauseAfter?: number }>
): Promise<void> => {
  let fullText = '';

  for (const segment of segments) {
    await new Promise<void>(resolve => {
      fullText += segment.text;
      typeText(element, fullText, undefined, { onComplete: resolve });
    });

    if (segment.pauseAfter) {
      await new Promise(resolve => setTimeout(resolve, segment.pauseAfter! * 1000));
    }
  }
};

/**
 * Staggered reveal of multiple elements
 * @param elements - Array of HTML elements
 * @param stagger - Delay between each element
 * @param duration - Animation duration for each element
 */
export const staggerReveal = (
  elements: HTMLElement[] | NodeListOf<Element>,
  stagger: number = 0.1,
  duration: number = 0.4
): gsap.core.Timeline => {
  const tl = gsap.timeline();

  if (prefersReducedMotion()) {
    gsap.set(elements, { opacity: 1, y: 0 });
    return tl;
  }

  gsap.set(elements, { opacity: 0, y: 20 });

  tl.to(elements, {
    opacity: 1,
    y: 0,
    duration,
    stagger,
    ease: 'power2.out',
  });

  return tl;
};

/**
 * Page transition - fade out current content
 * @param element - The page container element
 * @param duration - Transition duration
 */
export const pageTransitionOut = (
  element: HTMLElement,
  duration: number = 0.3
): gsap.core.Tween => {
  if (prefersReducedMotion()) {
    gsap.set(element, { opacity: 0 });
    return gsap.to(element, { duration: 0 });
  }

  return gsap.to(element, {
    opacity: 0,
    duration,
    ease: 'power2.in',
  });
};

/**
 * Page transition - fade in new content
 * @param element - The page container element
 * @param duration - Transition duration
 */
export const pageTransitionIn = (
  element: HTMLElement,
  duration: number = 0.3
): gsap.core.Tween => {
  if (prefersReducedMotion()) {
    gsap.set(element, { opacity: 1 });
    return gsap.to(element, { duration: 0 });
  }

  gsap.set(element, { opacity: 0 });

  return gsap.to(element, {
    opacity: 1,
    duration,
    ease: 'power2.out',
  });
};

/**
 * Corrupted/glitched text reveal - text appears with random corruption
 * @param element - The target HTML element
 * @param text - The text to reveal
 * @param corruptionLevel - How corrupted the text appears (0-1)
 */
export const corruptedReveal = async (
  element: HTMLElement,
  text: string,
  corruptionLevel: number = 0.3
): Promise<void> => {
  if (prefersReducedMotion()) {
    element.textContent = text;
    return;
  }

  const corruptChar = (char: string): string => {
    if (char === ' ') return ' ';
    if (Math.random() < corruptionLevel) {
      return '█';
    }
    return char;
  };

  // First pass - heavily corrupted
  element.textContent = text.split('').map(() =>
    Math.random() > 0.5 ? '█' : getRandomChar()
  ).join('');

  await new Promise(resolve => setTimeout(resolve, 100));

  // Second pass - partially corrupted
  element.textContent = text.split('').map(char => corruptChar(char)).join('');

  await new Promise(resolve => setTimeout(resolve, 150));

  // Third pass - mostly clear
  element.textContent = text.split('').map(char =>
    Math.random() < 0.1 ? '█' : char
  ).join('');

  await new Promise(resolve => setTimeout(resolve, 100));

  // Final reveal
  element.textContent = text;
};

/**
 * Vector notation typing - special effect for AI narrator passages
 * Types numbers with slight variations for unsettling effect
 * @param element - The target HTML element
 * @param vector - Array of numbers or the vector string
 */
export const typeVector = async (
  element: HTMLElement,
  vector: number[] | string,
  variablePrefix: string = 'dim(carlos) = '
): Promise<void> => {
  if (prefersReducedMotion()) {
    const vectorStr = typeof vector === 'string'
      ? vector
      : `[${vector.map(n => n.toFixed(2)).join(', ')}]`;
    element.textContent = variablePrefix + vectorStr;
    return;
  }

  // Type the prefix
  await new Promise<void>(resolve => {
    typeText(element, variablePrefix, undefined, { onComplete: resolve });
  });

  // Type the vector with slight delays between numbers
  const nums = typeof vector === 'string'
    ? vector
    : `[${vector.map(n => n.toFixed(2)).join(', ')}, ...]`;

  await new Promise<void>(resolve => {
    typeText(element, variablePrefix + nums, undefined, { onComplete: resolve });
  });
};

/**
 * Create an entry sequence timeline for the intro
 * @param config - Configuration for the sequence
 */
export const createEntrySequence = (config: {
  container: HTMLElement;
  onSequenceComplete?: () => void;
  skipDelay?: number;
}): gsap.core.Timeline => {
  const { onSequenceComplete, skipDelay = 20 } = config;
  const tl = gsap.timeline({
    onComplete: onSequenceComplete,
  });

  if (prefersReducedMotion()) {
    // Immediately show final state
    onSequenceComplete?.();
    return tl;
  }

  // The timeline will be populated by the entry page logic
  // This is a factory that creates the base timeline

  // Auto-skip after delay
  if (skipDelay > 0) {
    setTimeout(() => {
      if (tl.isActive()) {
        tl.progress(1);
        onSequenceComplete?.();
      }
    }, skipDelay * 1000);
  }

  return tl;
};

/**
 * Utility: Kill all running animations on an element
 * @param element - The target element
 */
export const killAnimations = (element: HTMLElement): void => {
  gsap.killTweensOf(element);
};

/**
 * Utility: Set initial state for animation (hidden)
 * @param element - The target element
 */
export const setHidden = (element: HTMLElement): void => {
  gsap.set(element, { opacity: 0 });
};

/**
 * Utility: Set initial state for animation (visible)
 * @param element - The target element
 */
export const setVisible = (element: HTMLElement): void => {
  gsap.set(element, { opacity: 1 });
};

/**
 * Create a cursor element and append it to the container
 * @param container - The container to append the cursor to
 * @param cursorChar - The character to use for the cursor (default: _)
 */
export const createCursor = (
  container: HTMLElement,
  cursorChar: string = '_'
): HTMLSpanElement => {
  const cursor = document.createElement('span');
  cursor.className = 'cursor';
  cursor.textContent = cursorChar;
  container.appendChild(cursor);

  // Start blinking animation
  createBlinkingCursor(cursor);

  return cursor;
};

/**
 * Fade in an element
 * @param element - The target element
 * @param duration - Duration in seconds
 */
export const fadeIn = (
  element: HTMLElement,
  duration: number = 0.3
): gsap.core.Tween => {
  if (prefersReducedMotion()) {
    gsap.set(element, { opacity: 1 });
    return gsap.to(element, { duration: 0 });
  }

  return gsap.to(element, {
    opacity: 1,
    duration,
    ease: 'power2.out',
  });
};

/**
 * Fade out an element
 * @param element - The target element
 * @param duration - Duration in seconds
 */
export const fadeOut = (
  element: HTMLElement,
  duration: number = 0.3
): gsap.core.Tween => {
  if (prefersReducedMotion()) {
    gsap.set(element, { opacity: 0 });
    return gsap.to(element, { duration: 0 });
  }

  return gsap.to(element, {
    opacity: 0,
    duration,
    ease: 'power2.in',
  });
};

/**
 * Instantly set text content (no animation)
 * Used for reduced motion accessibility
 * @param element - The target element
 * @param text - The text to display
 */
export const instantText = (element: HTMLElement, text: string): void => {
  element.textContent = text;
};

/**
 * Animation sequence builder for complex multi-step animations
 * Provides a simpler API for chaining animations
 */
export class AnimationSequence {
  private timeline: gsap.core.Timeline;

  constructor() {
    this.timeline = gsap.timeline({ paused: true });
  }

  /**
   * Add an animation step to the sequence
   * @param animation - Function that returns a GSAP animation or void
   * @param position - Position label (e.g., '+=0.5' for delay)
   */
  add(
    animation: () => gsap.core.Tween | gsap.core.Timeline | void,
    position?: string
  ): AnimationSequence {
    // Wrap the animation function to ensure it returns void for GSAP callback compatibility
    const wrapper = () => {
      animation();
    };

    if (position) {
      this.timeline.add(wrapper, position);
    } else {
      this.timeline.add(wrapper);
    }
    return this;
  }

  /**
   * Add a delay to the sequence
   * @param seconds - Delay duration in seconds
   */
  delay(seconds: number): AnimationSequence {
    this.timeline.add(() => {}, `+=${seconds}`);
    return this;
  }

  /**
   * Add a label to the timeline
   * @param name - Label name
   */
  label(name: string): AnimationSequence {
    this.timeline.addLabel(name);
    return this;
  }

  /**
   * Play the sequence
   */
  play(): Promise<void> {
    return new Promise(resolve => {
      this.timeline.eventCallback('onComplete', resolve);
      this.timeline.play();
    });
  }

  /**
   * Pause the sequence
   */
  pause(): void {
    this.timeline.pause();
  }

  /**
   * Resume the sequence
   */
  resume(): void {
    this.timeline.resume();
  }

  /**
   * Skip to end of sequence
   */
  skip(): void {
    this.timeline.progress(1);
  }

  /**
   * Kill the sequence
   */
  kill(): void {
    this.timeline.kill();
  }

  /**
   * Get the underlying GSAP timeline
   */
  getTimeline(): gsap.core.Timeline {
    return this.timeline;
  }
}

/**
 * Create a new animation sequence
 */
export const createSequence = (): AnimationSequence => {
  return new AnimationSequence();
};

// Export gsap for direct use when needed
export { gsap };
